/**
 * Actor Role Taxonomy for the Prioritization System
 *
 * Roles are scoped administrative positions only.
 * All domain-level capabilities (what used to be steward, criteria-authority,
 * cross-map-lead, facilitator, list-owner, strategist) are now expressed as
 * granular CapabilityId permissions assigned directly to users via
 * role_permissions in the database.
 */

// Actor Roles
export type ActorRole =
  | 'workspace-member'  // Baseline role — every workspace member holds this automatically
  | 'platform-admin'    // Full platform access - sees all tenants/workspaces
  | 'tenant-admin'      // Tenant-level admin - sees only their tenant
  | 'workspace-admin'   // Workspace-level admin - sees only their workspace
  | 'system-admin';     // Backward compatibility alias for platform-admin

/**
 * Controls how a role's permissions propagate through the org hierarchy.
 *
 * 'none'     → permissions apply ONLY at the exact scope assigned. Deny-by-default.
 *              Being placed in an org unit without an explicit grant at that scope = no access.
 * 'children' → permissions apply at the assigned node AND all descendant org units.
 *              The `inheritable` flag on a group_node_assignment can further restrict this.
 * 'all'      → permissions apply everywhere regardless of where assigned.
 *              Reserved for platform/tenant admin roles.
 */
/**
 * How far a role's grant reaches down the org tree.
 *
 *   'none'     — only at the exact scope where the role is assigned
 *   'children' — that org unit and everything beneath it
 *   'all'      — the whole workspace, wherever assigned
 *
 * Renamed from `CascadeMode` (2026-08-28). "Cascade" now names the downward
 * motion of a Ball through an organization; it can only mean one thing. The DB
 * column is still `roles.cascade_mode` and is deliberately NOT renamed — that
 * would be a migration for a naming tidy. The mapping happens at the read/write
 * boundary in permissionResolver and AdminRoles.
 */
export type ScopeReach = 'none' | 'children' | 'all';

/** Platform and system administrator are the same: full platform scope (with rare exceptions). */
export function isPlatformOrSystemAdmin(role: ActorRole): boolean {
  return role === 'platform-admin' || role === 'system-admin';
}

/** True if the role has admin power at some scope (platform, tenant, or workspace). */
export function isAdminRole(role: ActorRole): boolean {
  return role === 'platform-admin' || role === 'system-admin' || role === 'tenant-admin' || role === 'workspace-admin';
}

// Role metadata
export interface RoleDefinition {
  id: ActorRole;
  name: string;
  description: string;
  icon: string;
  color: string;
  borderColor: string;
  bgColor: string;
  /** How permissions for this role propagate through the org hierarchy. */
  scopeReach: ScopeReach;
}

// Role definitions with visual styling
export const roleDefinitions: Record<ActorRole, RoleDefinition> = {
  'workspace-member': {
    id: 'workspace-member',
    name: 'Workspace Member',
    description: 'Baseline role held by every workspace member — read access to items, cycles, sessions, and lists',
    icon: '👤',
    color: 'text-slate-600',
    borderColor: 'border-slate-400',
    bgColor: 'bg-slate-50',
    scopeReach: 'none',
  },
  'platform-admin': {
    id: 'platform-admin',
    name: 'Platform Administrator',
    description: 'Full platform access - manages all tenants, workspaces, users, and system configuration',
    icon: '🏛️',
    color: 'text-purple-600',
    borderColor: 'border-purple-500',
    bgColor: 'bg-purple-50',
    scopeReach: 'all',
  },
  'tenant-admin': {
    id: 'tenant-admin',
    name: 'Tenant Administrator',
    description: 'Manages users, workspaces, and configuration within their tenant',
    icon: '🏢',
    color: 'text-blue-600',
    borderColor: 'border-blue-500',
    bgColor: 'bg-blue-50',
    scopeReach: 'all',
  },
  'workspace-admin': {
    id: 'workspace-admin',
    name: 'Workspace Administrator',
    description: 'Manages users and configuration within their workspace',
    icon: '📁',
    color: 'text-green-600',
    borderColor: 'border-green-500',
    bgColor: 'bg-green-50',
    scopeReach: 'children',
  },
  'system-admin': {
    id: 'system-admin',
    name: 'Platform Administrator',
    description: 'Full platform access - manages users, groups, roles, and system configuration (alias for Platform Admin)',
    icon: '🔐',
    color: 'text-red-600',
    borderColor: 'border-red-500',
    bgColor: 'bg-red-50',
    scopeReach: 'all',
  }
};





